// Modules
import {
  FormControl,
  Validators,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  ElementRef,
  SimpleChanges,
  forwardRef,
  Output,
  EventEmitter,
} from '@angular/core';

// Rxjs
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// Interfaces
import { ICustomDropdown } from '@shared/interfaces';

// Custom validators
import { removeSpaces } from '@modules/shared/validators/removeSpaces';

@Component({
    selector: 'app-custom-dropdown',
    templateUrl: './custom-dropdown.component.html',
    styleUrls: ['./custom-dropdown.component.scss'],
    host: {
        '(document:click)': 'onClick($event)',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomDropdownComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class CustomDropdownComponent
  implements OnInit, OnDestroy, OnChanges, ControlValueAccessor
{
  @Output() onValueChanged = new EventEmitter<any>();
  @Output() onClickEvent = new EventEmitter<any>();
  // Inputs
  @Input() options: ICustomDropdown[] = [];
  @Input() loading: boolean = false;
  @Input() hasError: boolean = false;
  @Input() searchable: boolean = false;
  @Input() placeholder: string = '';
  @Input() allowCustomValue: boolean = false;
  @Input() searchPlaceholder: string = '';
  @Input() disabled: boolean = false;

  // Public variables
  public isDisabled: boolean = false;
  public searchControl: FormControl = new FormControl(
    '',
    Validators.compose([Validators.required, removeSpaces])
  );
  public dropdownOpened: boolean = false;
  public optionsAvailable: ICustomDropdown[] = [];
  public currentSelection: ICustomDropdown;

  // Private variables
  private _onChange: (value: any) => void = () => {};
  private _onTouched = () => {};

  // Subscriptions
  private _subscription$: Subscription[] = [];

  // Constructor
  constructor(private _eref: ElementRef) {}

  // On component init
  ngOnInit() {
    if (this.searchable) {
      this._subscription$.push(
        this.searchControl.valueChanges
          .pipe(debounceTime(300))
          .subscribe(val => {
            this.optionsAvailable = this.options.filter(el =>
              el.name.toLowerCase().includes(val.toLowerCase())
            );
            this.onValueChanged.emit(val);
          })
      );
    }
  }

  // On component changes
  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.options) {
      this.optionsAvailable = this.options;
    }
  }

  // On component destroy
  ngOnDestroy() {
    this._subscription$.forEach(sub => sub.unsubscribe());
  }

  // Public method to handle the clicks out of the component
  public onClick(event): void {
    if (
      !this._eref.nativeElement.contains(event.target) &&
      this.dropdownOpened
    ) {
      this.toggleDropdownStatus();
    }
  }

  // Public method to store the value from the formGroup
  public writeValue(value: any) {
    this.currentSelection = value || null;
  }

  // Public method to register a form control change
  public registerOnChange(fn: any) {
    this._onChange = fn;
  }

  // Public method to register a form control touched
  public registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  // Public method to handle disable option
  public setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  // Public method to handle the toggle status of the dropdown
  public toggleDropdownStatus(): void {
    if (!this.isDisabled) {
      if (this.dropdownOpened) {
        this.dropdownOpened = false;
        this._onTouched();
      } else {
        this.dropdownOpened = true;
        if (this.searchable) {
          setTimeout(() => {
            this._eref.nativeElement.querySelector('input').focus();
          });
        }
      }
      this.onClickEvent.emit();
    }
  }

  // Public method to select an option
  public onOptionSelected(option: ICustomDropdown): void {
    if (!this.isDisabled) {
      this._onChange(option);
      this._onTouched();
      this.currentSelection = option;

      // Remove filter
      if (this.searchable) this.searchControl.setValue('');

      // Toggle the dropdown
      this.toggleDropdownStatus();
    }
  }

  // Public method to select a custom option
  public onCustomOptionSelected(): void {
    const option: ICustomDropdown = {
      name: this.searchControl.value,
      value: null,
    };
    this.onOptionSelected(option);
  }
}
